// Página SupplierForm - Formulario para crear/editar proveedores
// Página completa para gestionar proveedores

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getSupplier, createSupplier, updateSupplier } from '../../services/suppliers.service'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './SupplierForm.css'

/**
 * Página SupplierForm
 * Formulario completo para crear o editar proveedores
 */
function SupplierForm() {
  const navigate = useNavigate()
  const { email } = useParams()
  const { currentUser } = useAuth()
  const isEditing = !!email

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  // Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    taxId: '',
    email: '',
    phone: '',
    phoneAlternate: '',
    addresses: [],
    primaryAddressIndex: 0,
    paymentTerms: '',
    deliveryTime: '',
    minimumOrder: '',
    active: true,
    rating: '',
    notes: ''
  })

  // Estado para nueva dirección
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    province: '',
    type: 'fiscal'
  })

  const fetchSupplierData = useCallback(async () => {
    try {
      setLoading(true)
      const supplier = await getSupplier(email)
      
      if (supplier) {
        setFormData({
          name: supplier.name || '',
          businessName: supplier.businessName || '',
          taxId: supplier.taxId || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          phoneAlternate: supplier.phoneAlternate || '',
          addresses: supplier.addresses || [],
          primaryAddressIndex: supplier.primaryAddressIndex !== undefined ? supplier.primaryAddressIndex : 0,
          paymentTerms: supplier.paymentTerms || '',
          deliveryTime: supplier.deliveryTime || '',
          minimumOrder: supplier.minimumOrder ? supplier.minimumOrder.toString() : '',
          active: supplier.active !== false,
          rating: supplier.rating ? supplier.rating.toString() : '',
          notes: supplier.notes || ''
        })
      }
    } catch (error) {
      console.error('Error cargando proveedor:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos del proveedor'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [email])

  useEffect(() => {
    if (isEditing) {
      fetchSupplierData()
    }
  }, [isEditing, fetchSupplierData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    // Validación básica
    if (!formData.name.trim()) {
      setNotification({
        type: 'error',
        message: 'El nombre del proveedor es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!formData.email.trim()) {
      setNotification({
        type: 'error',
        message: 'El email es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.trim())) {
      setNotification({
        type: 'error',
        message: 'El email no tiene un formato válido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      const supplierData = {
        ...formData,
        // Solo incluir createdBy al crear, no al actualizar
        ...(isEditing ? {} : { createdBy: currentUser?.uid || null }),
        lastUpdatedBy: currentUser?.uid || null
      }

      if (isEditing) {
        // Actualizar proveedor existente
        await updateSupplier(email, supplierData)
        
        setNotification({
          type: 'success',
          message: 'Proveedor actualizado correctamente'
        })
      } else {
        // Crear nuevo proveedor
        await createSupplier(supplierData)
        
        setNotification({
          type: 'success',
          message: `Proveedor "${formData.name}" creado correctamente`
        })
      }
      
      setTimeout(() => {
        navigate('/admin/proveedores')
      }, 1500)
    } catch (error) {
      console.error('Error guardando proveedor:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar proveedor: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleAddressChange = (e) => {
    const { name, value } = e.target
    setNewAddress(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAddAddress = () => {
    if (!newAddress.street.trim() || !newAddress.city.trim()) {
      setNotification({
        type: 'error',
        message: 'La calle y ciudad son requeridas para agregar una dirección'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, { ...newAddress }],
      primaryAddressIndex: prev.addresses.length === 0 ? 0 : prev.primaryAddressIndex
    }))

    setNewAddress({
      street: '',
      city: '',
      postalCode: '',
      province: '',
      type: 'fiscal'
    })
  }

  const handleRemoveAddress = (index) => {
    setFormData(prev => {
      const newAddresses = prev.addresses.filter((_, i) => i !== index)
      let newPrimaryIndex = prev.primaryAddressIndex

      // Ajustar índice principal si es necesario
      if (index === prev.primaryAddressIndex) {
        newPrimaryIndex = newAddresses.length > 0 ? 0 : null
      } else if (index < prev.primaryAddressIndex) {
        newPrimaryIndex = Math.max(0, prev.primaryAddressIndex - 1)
      }

      return {
        ...prev,
        addresses: newAddresses,
        primaryAddressIndex: newPrimaryIndex
      }
    })
  }

  const handleSetPrimaryAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      primaryAddressIndex: index
    }))
  }

  if (loading) {
    return (
      <div className="supplier-form-page">
        <div className="loading-state">
          <p>Cargando datos del proveedor...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="supplier-form-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="form-header">
        <div>
          <button
            className="back-button"
            onClick={() => navigate('/admin/proveedores')}
          >
            ← Volver
          </button>
          <h1>{isEditing ? 'Editar Proveedor' : 'Crear Nuevo Proveedor'}</h1>
          <p className="form-subtitle">
            {isEditing 
              ? 'Modifica la información del proveedor'
              : 'Completa los datos para crear un nuevo proveedor'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="supplier-form">
        {/* Información Básica */}
        <div className="form-section">
          <h2 className="section-title">Información Básica</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">
                Nombre del Proveedor <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej: Muebles del Sur S.A."
                required
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="businessName">Razón Social</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Razón social completa"
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxId">CUIT / CUIL</label>
              <input
                type="text"
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                placeholder="20-12345678-9"
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="proveedor@ejemplo.com"
                required
                disabled={saving || isEditing}
                className="form-input"
              />
              {isEditing && (
                <small className="form-hint">
                  El email no se puede modificar (se usa como identificador único)
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Información de Contacto */}
        <div className="form-section">
          <h2 className="section-title">Información de Contacto</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="phone">Teléfono Principal</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+54 9 11 1234-5678"
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneAlternate">Teléfono Alternativo</label>
              <input
                type="tel"
                id="phoneAlternate"
                name="phoneAlternate"
                value={formData.phoneAlternate}
                onChange={handleChange}
                placeholder="+54 9 11 9876-5432"
                disabled={saving}
                className="form-input"
              />
            </div>
          </div>

          {/* Direcciones */}
          <div className="addresses-section">
            <h3 className="subsection-title">Direcciones</h3>
            
            {formData.addresses.length > 0 && (
              <div className="addresses-list">
                {formData.addresses.map((address, index) => (
                  <div key={index} className="address-item">
                    <div className="address-header">
                      <div className="address-info">
                        <strong>
                          {address.type === 'fiscal' ? '📋 Fiscal' : 
                           address.type === 'entrega' ? '🚚 Entrega' : '📍 Otro'}
                        </strong>
                        {index === formData.primaryAddressIndex && (
                          <span className="primary-badge">Principal</span>
                        )}
                      </div>
                      <div className="address-actions">
                        {index !== formData.primaryAddressIndex && (
                          <button
                            type="button"
                            onClick={() => handleSetPrimaryAddress(index)}
                            className="btn-set-primary"
                            disabled={saving}
                          >
                            Establecer como principal
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveAddress(index)}
                          className="btn-remove-address"
                          disabled={saving}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="address-details">
                      <p>{address.street}, {address.city}, {address.province}</p>
                      {address.postalCode && <p>CP: {address.postalCode}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Formulario para agregar nueva dirección */}
            <div className="new-address-form">
              <h4>Agregar Nueva Dirección</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="addressType">Tipo de Dirección</label>
                  <select
                    id="addressType"
                    name="type"
                    value={newAddress.type}
                    onChange={handleAddressChange}
                    disabled={saving}
                    className="form-input"
                  >
                    <option value="fiscal">Fiscal</option>
                    <option value="entrega">Entrega</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="street">Calle y Número</label>
                  <input
                    type="text"
                    id="street"
                    name="street"
                    value={newAddress.street}
                    onChange={handleAddressChange}
                    placeholder="Av. Corrientes 1234"
                    disabled={saving}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="city">Ciudad</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={newAddress.city}
                    onChange={handleAddressChange}
                    placeholder="Buenos Aires"
                    disabled={saving}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="province">Provincia</label>
                  <input
                    type="text"
                    id="province"
                    name="province"
                    value={newAddress.province}
                    onChange={handleAddressChange}
                    placeholder="CABA"
                    disabled={saving}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">Código Postal</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={newAddress.postalCode}
                    onChange={handleAddressChange}
                    placeholder="C1000"
                    disabled={saving}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddAddress}
                    disabled={saving}
                    className="btn-add-address"
                  >
                    ➕ Agregar Dirección
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Información Comercial */}
        <div className="form-section">
          <h2 className="section-title">Información Comercial</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="paymentTerms">Condiciones de Pago</label>
              <input
                type="text"
                id="paymentTerms"
                name="paymentTerms"
                value={formData.paymentTerms}
                onChange={handleChange}
                placeholder="Ej: 30 días, Contado, etc."
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryTime">Tiempo de Entrega</label>
              <input
                type="text"
                id="deliveryTime"
                name="deliveryTime"
                value={formData.deliveryTime}
                onChange={handleChange}
                placeholder="Ej: 15 días hábiles"
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="minimumOrder">Pedido Mínimo</label>
              <input
                type="number"
                id="minimumOrder"
                name="minimumOrder"
                value={formData.minimumOrder}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
                disabled={saving}
                className="form-input"
              />
              <small className="form-hint">Monto mínimo de pedido (opcional)</small>
            </div>

            <div className="form-group">
              <label htmlFor="rating">Calificación</label>
              <input
                type="number"
                id="rating"
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                placeholder="1-5"
                min="1"
                max="5"
                step="0.1"
                disabled={saving}
                className="form-input"
              />
              <small className="form-hint">Calificación del 1 al 5 (opcional)</small>
            </div>
          </div>
        </div>

        {/* Configuración */}
        <div className="form-section">
          <h2 className="section-title">Configuración</h2>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                disabled={saving}
              />
              <span>Proveedor activo</span>
            </label>
            <small className="form-hint">
              Los proveedores inactivos no aparecerán en las listas de selección
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notas / Observaciones</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Información adicional sobre el proveedor..."
              rows={4}
              disabled={saving}
              className="form-input"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/proveedores')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : (isEditing ? 'Actualizar Proveedor' : 'Crear Proveedor')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default SupplierForm

